/**
 * Die Routen, die ein **eigenes Fenster** füllen — nicht die App-Hülle.
 *
 * Der Hauptprozess öffnet dafür jeweils ein kleines, rahmenloses Fenster und
 * lädt dasselbe Bundle mit einer anderen Adresse. Alles, was „beim Start
 * dorthin, wo der Nutzer zuletzt war" tut, muss diese Fenster in Ruhe lassen:
 * Sonst navigieren sie sich selbst weg, sobald sie aufgehen.
 *
 * Genau das passierte (Nutzer am Bild, 22.09.2026): Das Schnell-Notieren-
 * Fenster erschien kurz und wurde dann von der Listen-/Projektansicht
 * überschrieben. Für das Fokus-Fenster gab es die Ausnahme schon einzeln — die
 * anderen Fenster hatte sie vergessen. Deshalb steht hier EINE Liste statt
 * einer Ausnahme je Fenster.
 */
export const STANDALONE_WINDOW_ROUTES = ['/focus', '/quick-add'] as const

/**
 * Gehört diese Adresse zu einem eigenen Fenster?
 *
 * Geprüft wird beides — der Pfad des Routers **und** der Hash: Beim ersten
 * Aufbau steht die Adresse manchmal nur im Hash, weil der Router noch nicht
 * gelesen hat.
 */
export function isStandaloneWindowRoute(pathname: string, hash: string = window.location.hash): boolean {
  return STANDALONE_WINDOW_ROUTES.some(route =>
    pathname === route
    || pathname.startsWith(`${route}/`)
    || hash === `#${route}`
    || hash.startsWith(`#${route}?`)
    || hash.startsWith(`#${route}/`))
}

/**
 * Die Fenster, die ihre **eigene Kopfzeile** mitbringen — mit Ziehfläche und
 * Schließen-Knopf. Sie dürfen die App-Titelleiste nicht obendrauf bekommen.
 *
 * Bis zum 22.09.2026 stand dort nur das Fokus-Fenster. Das
 * Schnell-Notieren-Fenster bekam die App-Titelleiste (Seitenleisten-Schalter,
 * Suche, „Feedback geben") über seine eigene gesetzt — und weil `.quick-add`
 * mit `100vh` rechnet, schob diese zweite Leiste den Fuß samt „Erstellen"
 * aus dem Fenster heraus.
 *
 * `/focus` ist trotzdem gesondert: Die Pille bekommt auch den Fenster-RAHMEN
 * nicht, sie ist nur ein schwebender Streifen.
 */
export const OWN_CHROME_ROUTES = ['/focus', '/quick-add'] as const

/** Bringt dieses Fenster seine eigene Kopfzeile mit? */
export function hasOwnWindowChrome(hash: string = window.location.hash): boolean {
  return OWN_CHROME_ROUTES.some(route => hash === `#${route}` || hash.startsWith(`#${route}?`) || hash.startsWith(`#${route}/`))
}

/**
 * Fenster **ohne eigenen Sync**. Die Schnelleingabe lebt versteckt weiter (#234, F3); mit eigenem
 * Sync liefe dauerhaft eine zweite Maschine samt Realtime-Abo neben der des Hauptfensters. Was sie
 * anlegt, steht trotzdem in der Warteschlange — das Hauptfenster schiebt es hoch, sobald sie
 * `quick-add:created` meldet.
 */
export const PASSIVE_SYNC_ROUTES = ['/quick-add'] as const

/** Fährt dieses Fenster seinen eigenen Sync? */
export function syncsOnItsOwn(hash: string = window.location.hash): boolean {
  return !PASSIVE_SYNC_ROUTES.some(route => hash === `#${route}` || hash.startsWith(`#${route}?`) || hash.startsWith(`#${route}/`))
}
